import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getLatestRates, getTimeSeries, type Rates, type TimeSeries } from '@/lib/exchangeRates';
import { useSettings } from './SettingsContext';

type CurrencyContextType = {
  base: string;
  rates: Rates | null;
  loading: boolean;
  setBase: (c: string) => void;
  convert: (amount: number, from?: string, to?: string) => number;
  getHistorical: (symbol: string, start: string, end: string) => Promise<Record<string, number>>;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSettings();
  
  // Get account currency from settings
  const accountCurrency = settings?.currency || 'USD';
  
  // Initialize display currency: use stored preference, otherwise default to account currency
  const [base, setBaseState] = useState(() => {
    const stored = localStorage.getItem('display_currency');
    return stored || accountCurrency;
  });
  
  const [rates, setRates] = useState<Rates | null>(null);
  const [loading, setLoading] = useState(false);

  // Update display currency if account currency changes AND user hasn't set a custom display currency
  useEffect(() => {
    const stored = localStorage.getItem('display_currency');
    
    // If no custom display currency is set, follow the account currency
    if (!stored && settings?.currency) {
      setBaseState(settings.currency);
    }
  }, [settings?.currency]);

  // Load exchange rates when base currency changes
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        if (mounted) setLoading(true);
        const r = await getLatestRates(base);
        if (mounted) setRates(r);
      } catch (err) {
        console.error('Failed to load exchange rates:', err);
        if (mounted) setRates(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [base]);

  // Update display currency and persist to localStorage
  const setBase = useCallback((c: string) => {
    setBaseState(c);
    localStorage.setItem('display_currency', c);
  }, []);

  // Convert amount between currencies
  const convert = useCallback((amount: number, from?: string, to?: string) => {
    // Default: convert from account currency to display currency
    const fromCurrency = from || accountCurrency;
    const toCurrency = to || base;
    
    if (!rates) return amount;
    if (fromCurrency === toCurrency) return amount;
    
    // If converting from base currency
    if (fromCurrency === base) {
      const rateTo = rates[toCurrency] ?? 1;
      return amount * rateTo;
    }
    
    // If converting to base currency
    if (toCurrency === base) {
      const rateFrom = rates[fromCurrency] ?? 1;
      return amount / rateFrom;
    }
    
    // Converting between two non-base currencies
    const rateFrom = rates[fromCurrency] ?? 1;
    const rateTo = rates[toCurrency] ?? 1;
    const amountInBase = amount / rateFrom;
    return amountInBase * rateTo;
  }, [base, rates, accountCurrency]);

  const getHistorical = useCallback(async (symbol: string, start: string, end: string) => {
    const data: TimeSeries = await getTimeSeries(base, symbol, start, end);
    const normalized: Record<string, number> = {};
    Object.entries(data).forEach(([date, record]) => {
      const value = record[symbol];
      if (typeof value === 'number') normalized[date] = value;
    });
    return normalized;
  }, [base]);

  return (
    <CurrencyContext.Provider value={{ base, rates, loading, setBase, convert, getHistorical }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Hook to use currency context
export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}